import { useMemo, useState, type FormEvent } from "react";
import { Check, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { apiErrorMessage, cloudChangePassword } from "../api/client";
import { useToast } from "../toast/ToastContext";

type Strength = "empty" | "weak" | "fair" | "strong";

function scorePassword(pwd: string): Strength {
  if (!pwd) return "empty";
  let score = 0;
  if (pwd.length >= 8) score += 1;
  if (pwd.length >= 12) score += 1;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score += 1;
  if (/\d/.test(pwd)) score += 1;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
  if (score <= 2) return "weak";
  if (score <= 3) return "fair";
  return "strong";
}

const STRENGTH_LABEL: Record<Exclude<Strength, "empty">, string> = {
  weak: "偏弱",
  fair: "一般",
  strong: "较强",
};

export function ChangePasswordForm({
  accountEmail,
  onSuccess,
  onCancel,
  compact = false,
}: {
  accountEmail?: string | null;
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Tighter layout for drawer/modal. */
  compact?: boolean;
}) {
  const { toast } = useToast();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState("");

  const strength = useMemo(() => scorePassword(newPwd), [newPwd]);
  const checks = useMemo(
    () => [
      {
        id: "len",
        ok: newPwd.length >= 8,
        label: "至少 8 位",
      },
      {
        id: "diff",
        ok: Boolean(newPwd) && newPwd !== currentPwd,
        label: "与当前密码不同",
      },
      {
        id: "match",
        ok: Boolean(confirmPwd) && newPwd === confirmPwd,
        label: "两次输入一致",
      },
    ],
    [newPwd, currentPwd, confirmPwd],
  );
  const canSubmit =
    Boolean(currentPwd && newPwd && confirmPwd) &&
    checks.every((c) => c.ok) &&
    !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (!canSubmit) return;
    setBusy(true);
    try {
      await cloudChangePassword(currentPwd, newPwd);
      setDone(true);
      toast("密码已更新");
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
      onSuccess?.();
    } catch (err) {
      setFormError(apiErrorMessage(err, "修改密码失败"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className={`pwd-change${compact ? " pwd-change--compact" : ""}`}>
        <div className="pwd-change__success" role="status">
          <div className="pwd-change__success-icon" aria-hidden>
            <Check size={28} strokeWidth={2.25} />
          </div>
          <h2 className="pwd-change__success-title">密码已更新</h2>
          <p className="pwd-change__success-lead">
            下次登录请使用新密码。已登录的其它设备可能需要重新登录。
          </p>
          {onCancel ? (
            <button
              type="button"
              className="btn btn-primary pwd-change__submit"
              onClick={onCancel}
            >
              完成
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`pwd-change${compact ? " pwd-change--compact" : ""}`}>
      {!compact ? (
        <header className="pwd-change__hero">
          <div className="pwd-change__hero-icon" aria-hidden>
            <ShieldCheck size={22} strokeWidth={1.85} />
          </div>
          <div className="pwd-change__hero-copy">
            <h1 className="pwd-change__title">修改密码</h1>
            <p className="pwd-change__lead">
              保护账户凭据。建议使用较长且不易猜测的口令。
            </p>
          </div>
        </header>
      ) : null}

      {accountEmail ? (
        <div className="pwd-change__account" title={accountEmail}>
          <span className="pwd-change__account-label">当前账号</span>
          <span className="pwd-change__account-email">{accountEmail}</span>
        </div>
      ) : null}

      <form className="pwd-change__form" onSubmit={(e) => void submit(e)}>
        <PasswordField
          id="pwd-current"
          label="当前密码"
          autoComplete="current-password"
          value={currentPwd}
          visible={showCurrent}
          onVisibleChange={setShowCurrent}
          onChange={setCurrentPwd}
          disabled={busy}
        />
        <PasswordField
          id="pwd-new"
          label="新密码"
          autoComplete="new-password"
          value={newPwd}
          visible={showNew}
          onVisibleChange={setShowNew}
          onChange={setNewPwd}
          disabled={busy}
        />

        {newPwd ? (
          <div
            className="pwd-change__strength"
            aria-live="polite"
            data-level={strength}
          >
            <div className="pwd-change__strength-bars" aria-hidden>
              <span />
              <span />
              <span />
            </div>
            <span className="pwd-change__strength-label">
              强度 · {STRENGTH_LABEL[strength === "empty" ? "weak" : strength]}
            </span>
          </div>
        ) : null}

        <PasswordField
          id="pwd-confirm"
          label="确认新密码"
          autoComplete="new-password"
          value={confirmPwd}
          visible={showConfirm}
          onVisibleChange={setShowConfirm}
          onChange={setConfirmPwd}
          disabled={busy}
        />

        <ul className="pwd-change__checks" aria-label="密码要求">
          {checks.map((c) => (
            <li
              key={c.id}
              className={`pwd-change__check${c.ok ? " is-ok" : ""}`}
            >
              <span className="pwd-change__check-dot" aria-hidden />
              {c.label}
            </li>
          ))}
        </ul>

        {formError ? (
          <p className="pwd-change__error" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="pwd-change__actions">
          {onCancel ? (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={onCancel}
            >
              取消
            </button>
          ) : null}
          <button
            type="submit"
            className="btn btn-primary pwd-change__submit"
            disabled={!canSubmit}
          >
            {busy ? (
              <>
                <Loader2 size={16} className="pwd-change__spin" aria-hidden />
                更新中…
              </>
            ) : (
              "更新密码"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function PasswordField({
  id,
  label,
  value,
  visible,
  onVisibleChange,
  onChange,
  autoComplete,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  visible: boolean;
  onVisibleChange: (v: boolean) => void;
  onChange: (v: string) => void;
  autoComplete: string;
  disabled?: boolean;
}) {
  return (
    <div className="field pwd-change__field">
      <label htmlFor={id}>{label}</label>
      <div className="pwd-change__input-wrap">
        <input
          id={id}
          type={visible ? "text" : "password"}
          autoComplete={autoComplete}
          required
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••"
        />
        <button
          type="button"
          className="pwd-change__eye"
          aria-label={visible ? "隐藏密码" : "显示密码"}
          aria-pressed={visible}
          tabIndex={-1}
          onClick={() => onVisibleChange(!visible)}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}
